import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { fetch_email_preview, post_email_breakdown_send, post_email_outbox_send } from '../api';
import { email_error_view, preview_view } from '../email_logs/adapters';
import { PreviewContent } from '../email_logs/preview_dialog';
import {
  breakdown_send_body,
  confirm_error,
  mode_banner,
  outbox_send_body,
  plan_counts_text,
  plan_view,
  results_notice,
  results_view,
  send_count,
  send_failure_view,
  send_label
} from './logic';
import '../email_logs/email_logs.css';
import './email_send.css';

/*
 * The one confirm step for every manual payment email: dry run first (mode, recipients, warnings,
 * a preview of the first email), then the send, then the per-row results.
 * target: {type: 'outbox', ids} | {type: 'breakdown', pay_month, all} | {type: 'breakdown', pay_month, teacher_uuids}.
 */
const PLAN_FAILED_TEXT = 'Could not check what would be sent.';

const post_for = (target, opts) =>
  target.type === 'outbox'
    ? post_email_outbox_send(outbox_send_body(target.ids, opts))
    : post_email_breakdown_send(breakdown_send_body(target.pay_month, target, opts));

const ROW_CHIP = {
  ready: { tone: 'green', label: 'Will send' },
  held: { tone: 'orange', label: 'Held back' },
  blocked: { tone: 'gray', label: 'Cannot send' }
};

const PlanRow = ({ r }) => {
  const chip = r.warning
    ? { tone: ROW_CHIP[r.bucket].tone, label: r.warning.label }
    : ROW_CHIP[r.bucket];
  return (
    <li className={`tp-es-row${r.warning ? ' tp-es-row-warn' : ''}`}>
      <div className="tp-es-row-main">
        <div className="tp-es-row-name">{r.teacher_name}</div>
        <div className="tp-es-row-meta">
          {[r.kind_label, r.id ? `Email #${r.id}` : '', r.to_display ? `To ${r.to_display}` : '']
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      <span className="tp-es-row-chip">
        <StatusPill tone={chip.tone}>{chip.label}</StatusPill>
      </span>
      {r.warning && r.warning.text ? <div className="tp-es-row-note">{r.warning.text}</div> : null}
    </li>
  );
};

const ResultRow = ({ r }) => (
  <li className={`tp-es-row${r.outcome === 'refused' ? ' tp-es-row-warn' : ''}`}>
    <div className="tp-es-row-main">
      <div className="tp-es-row-name">{r.teacher_name}</div>
      <div className="tp-es-row-meta">
        {[r.kind_label, r.id ? `Email #${r.id}` : '', r.to_display ? `To ${r.to_display}` : '']
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
    <span className="tp-es-row-chip">
      <StatusPill tone={r.tone}>{r.outcome_label}</StatusPill>
    </span>
    <div className="tp-es-row-note">{r.note}</div>
  </li>
);

// The first email: the plan's own preview when the server sends one, else (breakdown) the
// statement preview of the first teacher that would get it.
const FirstPreview = ({ target, plan }) => {
  const [state, setState] = useState('idle');
  const [view, setView] = useState(() =>
    plan.preview
      ? preview_view({
          ...plan.preview,
          recipient_display: plan.first ? plan.first.to_display : '',
          delivery_mode: plan.mode,
          month_lifecycle: 'CLOSED',
          can_send: true
        })
      : null
  );
  const first = plan.first;
  const can_fetch = !plan.preview && target.type === 'breakdown' && first && first.teacher_uuid;

  useEffect(() => {
    if (!can_fetch) return undefined;
    let live = true;
    setState('loading');
    fetch_email_preview(target.pay_month, first.teacher_uuid)
      .then((d) => {
        if (!live) return;
        setView(preview_view(d));
        setState('ready');
      })
      .catch(() => {
        if (live) setState('error');
      });
    return () => {
      live = false;
    };
  }, [can_fetch, target.pay_month, first]);

  if (!first) return null;
  return (
    <div className="tp-es-preview tp-em-preview tp-em-dialog">
      <div className="tp-es-preview-hd">Preview of the first email: {first.teacher_name}</div>
      {state === 'loading' ? <CustomLoading /> : null}
      {state === 'error' ? (
        <div className="tp-hint">
          The preview could not be loaded. The email is still re-made from current data when sent.
        </div>
      ) : null}
      {view ? (
        <PreviewContent
          view={view}
          teacher_name={first.teacher_name}
          show_mode_banner={false}
          show_notices={false}
        />
      ) : null}
      {!view && state === 'idle' ? (
        <dl className="tp-em-meta">
          <dt>Email</dt>
          <dd>{first.kind_label || '—'}</dd>
          <dt>To</dt>
          <dd>{first.to_display || '—'}</dd>
        </dl>
      ) : null}
    </div>
  );
};

const SendDialog = ({ title, target, on_close, on_sent, on_error, on_notify }) => {
  const [phase, setPhase] = useState('planning'); // planning | plan | plan_error | sending | results
  const [plan, setPlan] = useState(null);
  const [plan_error, setPlanError] = useState('');
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [send_error, setSendError] = useState('');
  // The live switch moved since the preview (409 EMAIL_MODE_CHANGED): only a fresh preview can send.
  const [stale, setStale] = useState(false);
  const [results, setResults] = useState(null);
  const seq = useRef(0);
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;

  const load_plan = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setPhase('planning');
    setStale(false);
    setSendError('');
    post_for(target, { dry_run: true })
      .then((d) => {
        if (mine !== seq.current) return;
        setPlan(plan_view(d));
        setPhase('plan');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, PLAN_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error(e, PLAN_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setPlanError(v.text);
        setPhase('plan_error');
      });
    // target is fixed for the dialog's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load_plan();
  }, [load_plan]);

  const error = plan ? confirm_error(plan, force, reason) : '';
  const busy = phase === 'sending';

  const send = () => {
    setTouched(true);
    if (error || busy) return;
    setPhase('sending');
    setSendError('');
    post_for(target, {
      force: force && plan.counts.held > 0,
      reason,
      expected_mode: plan.mode
    })
      .then((d) => {
        const v = results_view(d, plan.mode);
        setResults(v);
        setPhase('results');
        const n = results_notice(v);
        if (on_notify) on_notify(n.type, n.text);
        if (on_sent) on_sent(v);
      })
      .catch((e) => {
        // Inline even on 403: the dry run needs the payment grant, the send a super user (server's sentence).
        const v = send_failure_view(e);
        setSendError(v.text);
        setStale(v.mode_changed);
        setPhase('plan');
      });
  };

  const n = plan ? send_count(plan, force) : 0;

  return (
    <TpDialog
      title={title}
      on_close={on_close}
      busy={busy}
      guard_text={phase === 'results' ? '' : discard_guard_text(reason)}
      size="lg"
    >
      <div className="tp-modal tp-es">
        {phase === 'planning' ? <CustomLoading /> : null}
        {phase === 'plan_error' ? <LoadFailed text={plan_error} on_retry={load_plan} /> : null}

        {plan && (phase === 'plan' || phase === 'sending') ? (
          <>
            <div
              className={`tp-banner ${plan.mode === 'LIVE' ? 'tp-banner-warn' : 'tp-banner-info'}`}
              role="status"
              data-mode={plan.mode}
            >
              {mode_banner(plan.mode, plan.test_inboxes, plan.payments_inbox)}
            </div>
            <div className="tp-es-counts">
              <b>{plan.counts.total}</b> {plan.counts.total === 1 ? 'email' : 'emails'}:{' '}
              {plan_counts_text(plan.counts)}
            </div>
            {plan.rows.length ? (
              <ul className="tp-es-rows" aria-label="What would be sent">
                {plan.rows.map((r) => (
                  <PlanRow key={r.key} r={r} />
                ))}
              </ul>
            ) : null}
            {plan.counts.held ? (
              <div className="tp-es-force">
                <label className="tp-es-force-check" htmlFor="tp-es-force">
                  <input
                    id="tp-es-force"
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                  />
                  <span>
                    Send the {plan.counts.held} held-back{' '}
                    {plan.counts.held === 1 ? 'email' : 'emails'} anyway
                  </span>
                </label>
                {force ? (
                  <div className="tp-field">
                    <label htmlFor="tp-es-reason">
                      Reason (required, recorded in the audit trail)
                    </label>
                    <textarea
                      id="tp-es-reason"
                      className="tp-textarea"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      onBlur={() => setTouched(true)}
                      placeholder="Why must this go again?"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <FirstPreview target={target} plan={plan} />
            {touched && error ? (
              <span className="tp-neg" role="alert">
                {error}
              </span>
            ) : null}
            {send_error ? (
              <span className="tp-neg" role="alert">
                {send_error}
              </span>
            ) : null}
            <div className="tp-modal-ft">
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={busy ? undefined : on_close}
                aria-disabled={busy ? 'true' : undefined}
              >
                Cancel
              </button>
              {stale ? (
                <button type="button" className="tp-btn" onClick={load_plan}>
                  Preview again
                </button>
              ) : (
                <button
                  type="button"
                  className="tp-btn"
                  onClick={busy || n === 0 ? () => setTouched(true) : send}
                  aria-disabled={busy || n === 0 ? 'true' : undefined}
                >
                  {busy ? 'Sending…' : send_label(n)}
                </button>
              )}
            </div>
          </>
        ) : null}

        {phase === 'results' && results ? (
          <>
            <div
              className={`tp-banner ${results.refused && !results.sent ? 'tp-banner-error' : 'tp-banner-ok'}`}
              role="status"
            >
              {results.summary}{' '}
              {results.mode === 'LIVE' ? 'Sent to teachers.' : 'Sent to the test inboxes only.'}
            </div>
            {results.mode_changed ? (
              <div className="tp-banner tp-banner-warn" role="alert">
                Live mode changed while you were confirming. These emails went in{' '}
                {results.mode === 'LIVE'
                  ? 'live mode, to the teachers'
                  : 'test mode, to the test inboxes'}
                .
              </div>
            ) : null}
            <ul className="tp-es-rows" aria-label="Send results">
              {results.rows.map((r) => (
                <ResultRow key={r.key} r={r} />
              ))}
            </ul>
            <div className="tp-modal-ft">
              <button type="button" className="tp-btn" onClick={on_close}>
                Done
              </button>
            </div>
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default SendDialog;
